import { OrganizationDetailFragment, useUpdateOrganization } from '@alice-financial/alice-sdk'
import { Grid, Button, HealthcheckItem, TextField } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useCurrentOrgRootPath } from '../organization/useOrg'
import { usePayrollPlatforms } from '../../organization/onboarding/usePayrollPlatforms'
import { useForm } from 'react-hook-form'
import { HealthcheckStatusResult } from '../types'
import { FormattedMessage } from 'react-intl'

type PayrollOpsSurveyHealthcheckProps = {
  org: OrganizationDetailFragment
  todo?: boolean
}

type OrganizationFormData = {
  defaultPayrollPlatformCode: string | null
}

export const DefaultPayrollPlatformHealthcheck = ({ org, todo }: PayrollOpsSurveyHealthcheckProps) => {
  const defaultPayrollPlatform = org.defaultPayrollPlatform
  const { data: payrollPlatforms, isLoading } = usePayrollPlatforms()
  const currentOrgRootPath = useCurrentOrgRootPath()
  const statusResult: Pick<HealthcheckStatusResult, 'status' | 'secondary'> = {
    status: defaultPayrollPlatform ? 'complete' : 'incomplete',
    secondary: defaultPayrollPlatform ? defaultPayrollPlatform.name : 'Choose your payroll platform',
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrganizationFormData>({
    mode: 'onChange',
    defaultValues: {
      defaultPayrollPlatformCode: defaultPayrollPlatform?.code,
    },
  })

  const { mutate: updateOrganization } = useUpdateOrganization()

  const onSubmit = async (data: OrganizationFormData) => {
    const input = {
      orgId: org.id,
      defaultPayrollPlatformCode: data.defaultPayrollPlatformCode,
    }
    // uses default onError/onSuccess notifications
    updateOrganization({ input })
  }

  return (
    <HealthcheckItem
      to={`${currentOrgRootPath}/payroll/platform`}
      primary="Payroll platform"
      secondary={statusResult.secondary}
      todo={todo}
      status={statusResult.status}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          {...register('defaultPayrollPlatformCode', { required: 'Payroll platform is required' })}
          label="Payroll platform"
          fullWidth
          select
          error={!!errors.defaultPayrollPlatformCode}
          helperText={
            (errors.defaultPayrollPlatformCode?.message as string) ||
            'If you operate with multiple payroll platforms, choose the one that should be the default for new pay groups.'
          }
          defaultValue=""
          disabled={isLoading}
        >
          <option value="" disabled>
            {isLoading ? 'Loading platforms...' : 'Select payroll platform'}
          </option>
          {payrollPlatforms?.map((platform) => (
            <option
              key={platform.id}
              value={platform.code}
              selected={platform.code === defaultPayrollPlatform?.code}
            >
              {platform.name}
            </option>
          ))}
        </TextField>
        <Grid container flexDirection="row-reverse" sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" size="small">
            <FormattedMessage id="common.submit" />
          </Button>
        </Grid>
      </form>
    </HealthcheckItem>
  )
}
